import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createCheckout, isShopifyConnected } from '../lib/shopify';
import type { CartLine, Product } from '../types/commerce';
type CheckoutState = 'idle' | 'loading' | 'unavailable' | 'error';
interface CartContextValue {
  lines: CartLine[];
  count: number;
  subtotal: number;
  currencyCode: string;
  isOpen: boolean;
  checkoutState: CheckoutState;
  open: () => void;
  close: () => void;
  add: (product: Product, quantity?: number) => void;
  setQuantity: (handle: string, quantity: number) => void;
  remove: (handle: string) => void;
  checkout: () => Promise<void>;
}
const CartContext = createContext<CartContextValue | null>(null);
const STORAGE_KEY = 'beat-cart';
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lines, setLines] = useState<CartLine[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) as CartLine[] : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [checkoutState, setCheckoutState] = useState<CheckoutState>('idle');
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
    } catch {
      /* storage unavailable — cart stays in memory */
    }
  }, [lines]);
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const add = useCallback((product: Product, quantity = 1) => {
    setLines(current => {
      const existing = current.find(line => line.product.handle === product.handle);
      if (existing) {
        return current.map(line => line.product.handle === product.handle ? {
          ...line,
          quantity: line.quantity + quantity
        } : line);
      }
      return [...current, {
        product,
        quantity
      }];
    });
    setCheckoutState('idle');
    setIsOpen(true);
  }, []);
  const setQuantity = useCallback((handle: string, quantity: number) => {
    setLines(current => quantity <= 0 ? current.filter(line => line.product.handle !== handle) : current.map(line => line.product.handle === handle ? {
      ...line,
      quantity
    } : line));
  }, []);
  const remove = useCallback((handle: string) => {
    setLines(current => current.filter(line => line.product.handle !== handle));
  }, []);
  const checkout = useCallback(async () => {
    if (lines.length === 0) return;
    if (!isShopifyConnected) {
      setCheckoutState('unavailable');
      return;
    }
    setCheckoutState('loading');
    try {
      const url = await createCheckout(lines);
      if (!url) throw new Error('No checkout url');
      window.location.href = url;
    } catch {
      setCheckoutState('error');
    }
  }, [lines]);
  const value = useMemo<CartContextValue>(() => {
    const subtotal = lines.reduce((total, line) => total + line.product.price.amount * line.quantity, 0);
    return {
      lines,
      count: lines.reduce((total, line) => total + line.quantity, 0),
      subtotal,
      currencyCode: lines[0]?.product.price.currencyCode ?? 'SEK',
      isOpen,
      checkoutState,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      add,
      setQuantity,
      remove,
      checkout
    };
  }, [lines, isOpen, checkoutState, add, setQuantity, remove, checkout]);
  return <CartContext.Provider value={value} data-id="element-308" data-mp="2edujtg0njomi">{children}</CartContext.Provider>;
}
export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used inside CartProvider');
  return context;
}